import { integrationResourcesApi } from '../api/IntegrationResourcesApi'
import type {
  BigQueryDataset,
  BigQueryTable,
  IntegrationResourceContext,
} from '../models/integrationResources.types'

import { useAccount, useWorkspace } from '@/features/account'

export interface UseBigQueryBrowser {
  listDatasets: (projectId: string, signal?: AbortSignal) => Promise<BigQueryDataset[]>
  listTables: (
    projectId: string,
    datasetId: string,
    signal?: AbortSignal,
  ) => Promise<BigQueryTable[]>
  listFields: (
    projectId: string,
    datasetId: string,
    tableId: string,
    signal?: AbortSignal,
  ) => Promise<string[]>
}

export function useBigQueryBrowser(): UseBigQueryBrowser {
  const { accountId } = useAccount()
  const { workspaceId } = useWorkspace()

  function context(): IntegrationResourceContext | null {
    const selectedAccountId = accountId.value
    const selectedWorkspaceId = workspaceId.value
    if (selectedAccountId === undefined || selectedWorkspaceId === undefined) return null
    return { accountId: selectedAccountId, workspaceId: selectedWorkspaceId }
  }

  function withContext<T>(read: (selected: IntegrationResourceContext) => Promise<T>): Promise<T> {
    const selected = context()
    return selected
      ? read(selected)
      : Promise.reject(new Error('BigQuery browsing requires an active account and workspace'))
  }

  return {
    listDatasets: (projectId, signal) =>
      withContext((selected) => integrationResourcesApi.listDatasets(selected, projectId, signal)),
    listTables: (projectId, datasetId, signal) =>
      withContext((selected) =>
        integrationResourcesApi.listTables(selected, projectId, datasetId, signal),
      ),
    listFields: (projectId, datasetId, tableId, signal) =>
      withContext((selected) =>
        integrationResourcesApi.listFields(selected, projectId, datasetId, tableId, signal),
      ),
  }
}
